import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  X, 
  Layers, 
  Plus, 
  ChevronRight, 
  Circle
} from 'lucide-react';

/**
 * Compound State Editor - Create/Edit Nested States
 * 
 * Compound states contain child states and have their own initial state.
 * This editor allows:
 * - Converting atomic states to compound
 * - Adding child states
 * - Setting internal initial state
 */
const CompoundStateEditor = ({ 
  state = null,
  existingStates = [],
  machine = null,
  onSave, 
  onCancel,
  inline = true
}) => {
  const [name, setName] = useState(state?.id || '');
  const [childStates, setChildStates] = useState([]);
  const [internalInitial, setInternalInitial] = useState('');
  const [newChildName, setNewChildName] = useState('');
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (state) {
      setName(state.id || '');
      // Load existing child states if this is already a compound state
      if (state.type === 'compound' && state.states) {
        setChildStates(Object.keys(state.states));
        setInternalInitial(state.initial || '');
      }
    }
  }, [state]);

  const validate = () => {
    const errs = [];
    if (!name.trim()) {
      errs.push('State name is required');
    }
    if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(name)) {
      errs.push('State name must start with letter and contain only alphanumeric characters');
    }
    if (childStates.length === 0) {
      errs.push('Compound state must have at least one child state');
    }
    if (!internalInitial && childStates.length > 0) {
      errs.push('Must select an initial child state');
    }
    setErrors(errs);
    return errs.length === 0;
  };

  const handleAddChild = () => {
    if (!newChildName.trim()) return;
    if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(newChildName)) {
      setErrors(['Child state name must start with letter and contain only alphanumeric characters']);
      return;
    }
    if (childStates.includes(newChildName)) {
      setErrors(['Child state already exists']);
      return;
    }
    
    setChildStates(prev => [...prev, newChildName]);
    if (childStates.length === 0) {
      setInternalInitial(newChildName);
    }
    setNewChildName('');
    setErrors([]);
  };

  const handleRemoveChild = (childName) => {
    setChildStates(prev => prev.filter(c => c !== childName));
    if (internalInitial === childName) {
      setInternalInitial(childStates.filter(c => c !== childName)[0] || '');
    }
  };

  const handleSave = () => {
    if (!validate()) return;
    
    // Build compound state structure
    const childStateObjects = {};
    childStates.forEach(childName => {
      childStateObjects[childName] = {
        id: childName,
        type: 'atomic',
        transitions: [],
        entry: [],
        exit: []
      };
    });

    onSave({
      id: name,
      type: 'compound',
      initial: internalInitial,
      states: childStateObjects,
      transitions: state?.transitions || [],
      entry: state?.entry || [],
      exit: state?.exit || []
    });
  };

  if (inline) {
    return (
      <div 
        className="bg-[#1a1f3a] border-2 border-yellow-500 rounded-lg p-3 shadow-xl z-50 w-80"
        data-testid="compound-state-editor"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <Layers className="w-4 h-4 text-yellow-400" />
            <h3 className="text-sm font-bold">{state ? 'Edit Compound State' : 'Create Compound State'}</h3>
          </div>
          <button onClick={onCancel} className="text-gray-500 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          {/* Parent State Name */}
          <div>
            <Label className="text-xs">Parent State Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="ParentState"
              className="mt-1 h-8 text-sm"
              autoFocus
              disabled={!!state}
              data-testid="compound-name-input"
            />
          </div>

          {/* Child States */}
          <div>
            <Label className="text-xs flex items-center">
              Child States
              <Badge variant="outline" className="ml-2 text-[9px] h-4">
                {childStates.length} states
              </Badge>
            </Label>
            
            {/* Add new child */}
            <div className="flex space-x-2 mt-2">
              <Input
                value={newChildName}
                onChange={(e) => setNewChildName(e.target.value)}
                placeholder="ChildStateName"
                className="h-7 text-xs"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddChild();
                  }
                }}
              />
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2"
                onClick={handleAddChild}
              >
                <Plus className="w-3 h-3" />
              </Button>
            </div>

            {/* Child state list */}
            <ScrollArea className="h-28 mt-2 border border-white/10 rounded p-2">
              {childStates.length === 0 ? (
                <div className="text-xs text-gray-500 text-center py-4">
                  No child states yet. Add at least one.
                </div>
              ) : (
                <div className="space-y-1">
                  {childStates.map((child) => (
                    <div 
                      key={child}
                      className="flex items-center justify-between py-1 px-2 hover:bg-white/5 rounded"
                    >
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          checked={internalInitial === child}
                          onCheckedChange={() => setInternalInitial(child)}
                          id={`initial-${child}`}
                        />
                        <label 
                          htmlFor={`initial-${child}`}
                          className="text-xs font-mono cursor-pointer flex items-center"
                        >
                          <ChevronRight className="w-3 h-3 mr-1 text-gray-500" />
                          {child}
                          {internalInitial === child && (
                            <Badge variant="outline" className="ml-2 text-[8px] h-3 px-1 text-green-400 border-green-500/30">
                              initial
                            </Badge>
                          )}
                        </label>
                      </div>
                      <button
                        onClick={() => handleRemoveChild(child)}
                        className="text-gray-500 hover:text-red-400"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Visual Preview */}
          <div className="bg-black/30 rounded p-2 border border-white/10">
            <div className="text-[10px] text-gray-500 mb-1">Preview</div>
            <div className="flex items-center space-x-1 text-xs">
              <Layers className="w-3 h-3 text-yellow-400" />
              <span className="font-mono">{name || 'ParentState'}</span>
              <span className="text-gray-500">→</span>
              <span className="text-gray-400">
                {childStates.length > 0 ? childStates.join(', ') : '...'}
              </span>
            </div>
          </div>

          {/* Errors */}
          {errors.length > 0 && (
            <div className="text-xs text-red-400 space-y-1">
              {errors.map((err, idx) => (
                <div key={idx}>• {err}</div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-2 pt-2">
            <Button 
              onClick={handleSave} 
              size="sm" 
              className="flex-1 h-8 text-xs bg-yellow-600 hover:bg-yellow-700"
              data-testid="compound-save-button"
            >
              <Layers className="w-3 h-3 mr-1" />
              {state ? 'Update' : 'Create Compound'}
            </Button>
            <Button 
              onClick={onCancel} 
              size="sm" 
              variant="outline" 
              className="h-8 text-xs"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default CompoundStateEditor;
